import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import swal from "sweetalert";
import HttpCommon from "../../Utils/http-common";
import {
  LOGIN_TYPE,
  MUST_SET_PASSWORD_KEY,
  TOKEN_KEY,
  USERID_KEY,
} from "../../Frontend/Constant";
import {
  APP_OPEN_WAIT_MS,
  buildAppWhitelistDeepLink,
  normalizeWhitelistToken,
  shouldAttemptAppDeepLink,
  shouldAutoConsumeOnWeb,
  tryOpenAppDeepLink,
} from "./whitelistLoginBridge";

/** Survives React Strict Mode remounts within the same page load. */
let consumeInFlightKey = null;

function goToUserInformation() {
  window.location.href = "/user-information";
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function WhitelistLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const finishedRef = useRef(false);
  const [phase, setPhase] = useState("loading"); // loading | opening_app | app_hint | signing_in
  const [token, setToken] = useState(null);
  const [deepLink, setDeepLink] = useState(null);

  const consumeOnWeb = async (sessionToken) => {
    if (consumeInFlightKey === sessionToken) return;
    consumeInFlightKey = sessionToken;
    setPhase("signing_in");

    try {
      const res = await HttpCommon.post("/whitelist-consume-token", {
        token: sessionToken,
      });
      if (res?.data?.isAuthenticated || res?.data?.success === true || res?.data?.success === 1) {
        const user_id = res.data.user?.id ?? res.data.user_id;
        const authToken = res.data.token;
        localStorage.setItem(TOKEN_KEY, authToken);
        localStorage.setItem(USERID_KEY, user_id);
        localStorage.setItem(LOGIN_TYPE, "user_login");
        if (res.data.must_set_password) {
          localStorage.setItem(MUST_SET_PASSWORD_KEY, "true");
        }
        finishedRef.current = true;
        goToUserInformation();
        return;
      }
      if (finishedRef.current) return;
      const failMsg = res?.data?.message || res?.data?.err || "Unable to sign in.";
      swal("Unable to sign in", failMsg, "error").then(() => navigate("/members"));
    } catch (err) {
      if (finishedRef.current) return;

      const existingToken = localStorage.getItem(TOKEN_KEY);
      const existingUserId = localStorage.getItem(USERID_KEY);

      if (existingToken && existingUserId) {
        finishedRef.current = true;
        goToUserInformation();
        return;
      }

      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
        return;
      }

      const msg =
        err?.response?.data?.message || "Link expired or invalid. Please try again.";
      swal("Unable to sign in", msg, "error").then(() => navigate("/members"));
    } finally {
      if (consumeInFlightKey === sessionToken) {
        consumeInFlightKey = null;
      }
    }
  };

  useEffect(() => {
    const normalized = normalizeWhitelistToken(searchParams.get("token"));
    setToken(normalized);

    if (!normalized) {
      swal("Invalid link", "Please request a new sign-in link from login.", "error").then(
        () => navigate("/members"),
      );
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    (async () => {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const attemptApp = shouldAttemptAppDeepLink(ua);
      const appLink = buildAppWhitelistDeepLink(normalized);
      setDeepLink(appLink);

      if (attemptApp && appLink) {
        setPhase("opening_app");
        tryOpenAppDeepLink(appLink, { userAgent: ua });
        await sleep(APP_OPEN_WAIT_MS);
        if (cancelled) return;
        setPhase("app_hint");
        return;
      }

      if (cancelled) return;
      if (shouldAutoConsumeOnWeb({ attemptedApp: attemptApp })) {
        await consumeOnWeb(normalized);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        px: 2,
        textAlign: "center",
      }}
    >
      {(phase === "loading" || phase === "opening_app" || phase === "signing_in") && (
        <>
          <CircularProgress style={{ color: "black" }} />
          <Typography variant="body2" color="text.secondary">
            {phase === "opening_app"
              ? "Trying to open the FootprintID app…"
              : phase === "signing_in"
                ? "Signing you in…"
                : "Loading…"}
          </Typography>
        </>
      )}

      {phase === "app_hint" && (
        <>
          <Typography variant="h6">Open the FootprintID app</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
            The app is installed, finish signing in there. If it did not open, tap the
            button below. This link can only be used once, so do not continue in the
            browser unless you want to set your password on the website.
          </Typography>
          <Button
            variant="contained"
            href={deepLink || undefined}
            sx={{ mt: 1 }}
          >
            Open FootprintID app
          </Button>
          <Button
            variant="text"
            onClick={() => token && consumeOnWeb(token)}
          >
            Continue in browser
          </Button>
        </>
      )}
    </Box>
  );
}
